import React from 'react';
import { Info } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import { agoText, formatDateTime, formatDay, presenceView, ucfirst } from './time';
import './Student360.css';

/** The exact moment behind a relative time, in Tashkent time (a day precision viewer gets a date only). */
function exactText(view, exact, t, lang) {
  if (!exact) return '';
  return view.precision === 'day'
    ? formatDay(exact, lang)
    : `${formatDateTime(exact, lang)} · ${t('student360.presence.tashkentTime')}`;
}

/** The words of a presence view: "Online now", "3 h ago", "5 d ago", "Never seen" (and the tooltip lines). */
export function presenceLabel(t, view) {
  const hints = [];
  let text;
  if (view.online) {
    text = t('student360.presence.online');
    hints.push(t('student360.presence.onlineHint'));
  } else if (view.ago) {
    text = ucfirst(agoText(t, view.ago));
  } else {
    text = t('student360.presence.never');
    hints.push(t('student360.presence.neverHint'));
  }
  return { text, hints };
}

/**
 * Last seen as a coloured dot and words: "Online now", "3 h ago", "5 d ago", "Never seen". The dot
 * follows the at-risk threshold (green online or today, amber 1-6 days, red 7 days or more). A
 * viewer at day precision gets "today", "yesterday" or "N d ago", never a time of day or "online".
 * `approximate` values (taken from the last study activity) carry a small info icon and say so in the tooltip.
 * `now` is the response's generatedAt as epoch ms (see nowFrom).
 */
function PresenceBadge({ presence, now }) {
  const { t, lang } = useI18n();
  const view = presenceView(presence, now);
  if (!view) return null;

  const { text, hints } = presenceLabel(t, view);
  const exact = exactText(view, view.exact, t, lang);
  if (exact) hints.unshift(exact);
  if (view.approximate) hints.push(t('student360.presence.approximateHint'));

  return (
    <span className={`s360-presence is-${view.tone}`} title={hints.join('\n')}>
      <span className="s360-dot" aria-hidden="true" />
      <span className="s360-presence-text">{text}</span>
      {view.approximate && <Info className="s360-approx" size={13} aria-hidden="true" />}
    </span>
  );
}

/**
 * Last sign-in in words. It is empty ("—") until the student signs in once after the release: that
 * is not the same as never seen, and the tooltip says so. `labelled` writes it as one short line
 * ("Kirish: 2 soat oldin") for the cell that sits under the last-seen words.
 */
export function LastLogin({ presence, now, labelled = false }) {
  const { t, lang } = useI18n();
  const view = presenceView(presence, now);
  if (!view) return null;
  if (!view.loginExact) {
    return (
      <span className="s360-login s360-muted" title={t('student360.presence.loginUnknownHint')}>
        {labelled ? t('student360.presence.loginAgo', { when: '—' }) : '—'}
      </span>
    );
  }
  return (
    <span className="s360-login" title={exactText(view, view.loginExact, t, lang)}>
      {labelled
        ? t('student360.presence.loginAgo', { when: agoText(t, view.login) })
        : ucfirst(agoText(t, view.login))}
    </span>
  );
}

export default PresenceBadge;
